import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Progress } from './ui/progress';
import { Target, TrendingUp, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { HelpTooltip } from './HelpTooltip';
import { API_CACHE_EVENT } from '../lib/apiCache';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const QUARTER_LABELS = {
  1: 'Sty–Mar',
  2: 'Kwi–Cze',
  3: 'Lip–Wrz',
  4: 'Paź–Gru',
};

export const KPISidebar = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const isRestrictedRole = user?.role === 'montaz' || user?.role === 'duch' || user?.role === 'guest';

  useEffect(() => {
    if (isRestrictedRole) { setLoading(false); return; }

    const fetchKPI = async () => {
      try {
        const response = await axios.get(`${API_URL}/reports/kpi`);
        setData(response.data);
      } catch (error) {
        console.error('KPI fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchKPI();
    const interval = setInterval(fetchKPI, 5 * 60 * 1000);
    window.addEventListener(API_CACHE_EVENT, fetchKPI);
    return () => { clearInterval(interval); window.removeEventListener(API_CACHE_EVENT, fetchKPI); };
  }, [isRestrictedRole]);

  if (isRestrictedRole) return null;

  if (loading || !data) {
    return (
      <div className="px-4 py-3">
        <div className="bg-[var(--crm-bg-main)] rounded-lg p-3 animate-pulse">
          <div className="h-4 bg-[var(--crm-bg-btn-secondary)] rounded w-3/4 mb-2"></div>
          <div className="h-6 bg-[var(--crm-bg-btn-secondary)] rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (data.access_denied) return null;

  const goalReached = data.goal_progress_percent >= 100;
  const hasGoal = data.has_personal_goal !== false && data.monthly_goal > 0;
  const qNum = data.quarter_num || Math.ceil((new Date().getMonth() + 1) / 3);
  const qLabel = QUARTER_LABELS[qNum] || '';
  const isB2B = user?.role === 'handlowiec_b2b';

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '0';
    if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
    return value.toFixed(0);
  };

  const getProgressColor = (percent) => {
    if (percent >= 100) return 'bg-emerald-500';
    if (percent >= 70) return 'bg-amber-500';
    if (percent >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getTextColor = (percent) => {
    if (percent >= 100) return 'text-emerald-400';
    if (percent >= 70) return 'text-amber-400';
    if (percent >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  return (
    <div className="px-4 py-3 border-t border-[var(--crm-border)]">
      <Link to="/kpi" className="block" data-testid="kpi-sidebar-widget">
        <div className="bg-[var(--crm-bg-main)] rounded-lg p-3 hover:bg-[var(--crm-bg-dialog)] transition-colors cursor-pointer">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Target className={`w-4 h-4 ${hasGoal ? getTextColor(data.goal_progress_percent) : 'text-[var(--crm-text-secondary)]'}`} />
              <span className="text-[var(--crm-text-secondary)] text-xs flex items-center gap-1">
                {hasGoal ? `Cel Q${qNum} (${qLabel})` : (isB2B ? 'Twoje wyniki' : 'Wyniki firmy')}
                <HelpTooltip text="Marża netto z opłaconych, w dostawie, w realizacji i zakończonych zleceń" />
              </span>
            </div>
            <ChevronRight className="w-4 h-4 text-[var(--crm-text-muted)]" />
          </div>

          {/* Main value + percentage */}
          <div className="flex items-baseline justify-between mb-2">
            <span className={`text-lg font-bold ${goalReached ? 'text-emerald-400' : 'text-[var(--crm-text-primary)]'}`} data-testid="kpi-sidebar-main-value">
              {isB2B ? (hasGoal ? 'Postęp celu' : 'Brak planu') : `${formatCurrency(data.current_month_net_margin)} zł`}
            </span>
            {hasGoal && (
              <span className={`text-sm font-bold ${getTextColor(data.goal_progress_percent)}`} data-testid="kpi-sidebar-percent">
                {isB2B ? data.goal_progress_percent : data.goal_progress_percent.toFixed(0)}%
              </span>
            )}
          </div>

          {/* Progress bar + goal info */}
          {hasGoal && (
            <>
              <Progress
                value={Math.min(data.goal_progress_percent, 100)}
                className="h-2 bg-[var(--crm-bg-btn-secondary)]"
                indicatorClassName={getProgressColor(data.goal_progress_percent)}
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[var(--crm-text-muted)] text-xs">
                  cel: {formatCurrency(data.monthly_goal)} zł
                </span>
                {!isB2B && (
                  <span className="text-[var(--crm-text-muted)] text-xs flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    {(data.avg_net_margin_percent || 0).toFixed(0)}% marży
                  </span>
                )}
              </div>
            </>
          )}

          {/* No goal fallback */}
          {!hasGoal && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-[var(--crm-text-muted)] text-xs">
                {data.total_projects || 0} zleceń · Q{qNum}
              </span>
              {!isB2B && (
                <span className="text-[var(--crm-text-muted)] text-xs flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {(data.avg_net_margin_percent || 0).toFixed(0)}%
                </span>
              )}
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};
